import React, { useEffect, useContext, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import * as Yup from 'yup';
import { buildUrl } from 'utils';
import { Formik, Field } from 'formik';
import { BastionContext } from 'context';
import { AuthLayout } from 'components/UI';
import { AuthActions } from 'state/modules/auth';
import { Link, useHistory } from 'react-router-dom';
import ArrowBack from '@material-ui/icons/ArrowBack';
import { useFusionAuth } from '@fusionauth/react-sdk';
import { setWithCredentials } from 'services/api/api';
import { BastionsSelect } from 'components/AsyncFields';
import { Box, Button, Typography } from '@mui/material';
import { TextField, ErrorMessage } from 'components/FormFields';

const LoginPage = () => {
  const history = useHistory();
  const formRef = useRef(null);
  const dispatch = useDispatch();

  const { theme } = useContext(BastionContext);
  const [step, setStep] = useState(0);

  const { loading, needsOTP, isAuthenticated, errors, bastions, currentBastion, currentClient } =
    useSelector((state) => state.auth);

  const { startLogin, startLogout, isLoggedIn } = useFusionAuth();

  const [shouldLogout, setShouldLogout] = useState(false);

  const LoginSchema = Yup.object().shape({
    email:
      step === 0 &&
      Yup.string().email(i18n.t('common.invalid_email')).required(i18n.t('common.invalid_email')),
    bastion_slug:
      bastions?.length > 1 && step === 1 && Yup.string().required(i18n.t('common.required_field')),
    password: step === 2 && Yup.string().required(i18n.t('common.required_field')),
  });

  useEffect(() => {
    if (needsOTP) {
      history.push('/mfa');
    }

    if (isAuthenticated) {
      const org = sessionStorage.getItem('org_slug');
      history.push(buildUrl('/dashboard', org));
    }
  }, [isAuthenticated, history, needsOTP]);

  useEffect(() => {
    return () => {
      dispatch(AuthActions.resetBastions());
      setStep(0);
    };
  }, [dispatch, setStep]);

  useEffect(() => {
    if (shouldLogout) return;
    if (isLoggedIn) {
      setWithCredentials(true);
      dispatch(AuthActions.oAuthLogin());
      const org = sessionStorage.getItem('org_slug');
      if (org) {
        history.push(buildUrl('/dashboard', org));
      } else {
        startLogout();
        setShouldLogout(true);
      }
    }
  }, [isLoggedIn, history, startLogout, dispatch, shouldLogout]);

  useEffect(() => {
    if (bastions?.length === 1 && step === 0) {
      chooseBastion(bastions[0].id_slug);
    } else if (bastions?.length > 1 && step === 0) {
      setStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bastions, step]);

  useEffect(() => {
    if (currentClient !== null) {
      console.log('SETTING STEP 3', currentClient);
      setStep(3);
    } else {
      setStep(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getButtonTitle = () => {
    switch (step) {
      case 0:
      case 1:
        return i18n.t('common.continue');
      case 2:
        return i18n.t('auth.login');
      case 3:
        return i18n.t('auth.login');
    }
  };

  const chooseBastion = useCallback(
    (idSlug) => {
      console.log('Choosing bastion:', idSlug);

      const bastion = bastions?.find((item) => item.id_slug === idSlug);
      const client = bastion?.clients?.find((item) => item.client_type === 'MCD');

      const email = formRef.current?.values?.email || '';
      formRef.current?.setFieldValue?.('bastion_slug', idSlug);

      dispatch(
        AuthActions.setCurrentBastion({
          bastion,
          client,
          email,
        })
      );

      if (client) {
        console.log('SETTING STEP 3 FROM CHOOSE BASTION');
        setStep(3);
      } else {
        console.log('SETTING STEP 2 FROM CHOOSE BASTION');
        setStep(2);
        dispatch(
          AuthActions.setCurrentBastion({
            bastion: null,
            client: null,
            email: null,
          })
        );
      }
    },
    [bastions, dispatch, setStep]
  );

  const onTryAgain = () => {
    dispatch(AuthActions.resetBastions());
  };

  const handleBack = () => {
    formRef.current.resetForm({ values: { email: '', bastion_slug: '', password: '' } });
    dispatch(AuthActions.setCurrentBastion({ bastion: null, email: null, client: null }));
    dispatch(AuthActions.resetBastions());
    setStep(0);
  };

  const submit = async (values) => {
    console.log('Submitting form at step:', values);
    switch (step) {
      case 0:
        console.log('Submitting form at step:', values);
        dispatch(AuthActions.fetchBastions(values.email));
        break;
      case 1:
        chooseBastion(values.bastion_slug);
        break;
      case 2:
        dispatch(AuthActions.login(values));
        break;
      case 3:
        startLogin('state-from-login');
        break;
    }
  };

  return (
    <AuthLayout title={`${theme.page.title} | Login`}>
      <Box data-cy="auth-fields-container" style={{ paddingTop: '30%' }} height="100%">
        <Formik
          validationSchema={LoginSchema}
          onSubmit={submit}
          innerRef={formRef}
          initialValues={{
            email: '',
            bastion_slug: '',
            password: '',
          }}
        >
          {({ handleSubmit }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Box mb={8}>
                <Typography component="div">
                  <Box fontSize={16} lineHeight={1} color="primary.main" fontWeight="bold">
                    {i18n.t('auth.welcome_to', { name: '' })}
                  </Box>
                  <Box mt={2} fontSize={32} lineHeight={1} fontWeight="bold">
                    {theme.page.title}
                  </Box>
                  <Box mt={1} fontSize={24} lineHeight={1} letterSpacing={6.6}>
                    {theme.page.secondTitle}
                  </Box>
                </Typography>
              </Box>
              {step === 1 && (
                <Link to="login" onClick={handleBack} data-cy="back-button">
                  <Box
                    fontSize={14}
                    display="flex"
                    alignItems="center"
                    fontWeight="Regular"
                    textTransform="capitalize"
                    color="scheduler.grayLight400"
                  >
                    <ArrowBack />
                    <Box ml={1}>{i18n.t('common.back')}</Box>
                  </Box>
                </Link>
              )}
              <Box>
                {/* INITIAL VIEW */}
                {console.log('STEP:', step)}
                {(step === 0 || step === 2) && (
                  <Box mt={1} display="flex" flexDirection="column">
                    <Field
                      type="email"
                      name="email"
                      component={TextField}
                      label={i18n.t('fields.email')}
                      disabled={errors?.errKey === 'RATELIMITEXCEEDED'}
                      attrs={{
                        'data-cy': 'email-input',
                      }}
                    />
                    {errors?.errKey === 'RATELIMITEXCEEDED' && (
                      <ErrorMessage hasIcon={false}>
                        {i18n.t('auth.auth_limit_error_text_1')}
                        <Box
                          component="span"
                          fontWeight="bold"
                          onClick={onTryAgain}
                          sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                        >
                          {i18n.t('auth.auth_limit_error_text_2')}
                        </Box>{' '}
                        {i18n.t('auth.auth_limit_error_text_3')}{' '}
                      </ErrorMessage>
                    )}
                  </Box>
                )}
                {/* STEP 2 LEGACY LOGIN */}
                {step === 2 && (
                  <>
                    <Box mt={1}>
                      <Field
                        type="password"
                        name="password"
                        component={TextField}
                        label={i18n.t('auth.password')}
                        attrs={{
                          'data-cy': 'password-input',
                        }}
                      />
                    </Box>
                    <Box display="flex" justifyContent="flex-end">
                      <Box
                        data-cy="forgot-password"
                        sx={{
                          '& a': {
                            color: 'primary.main',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            textDecoration: 'none',
                          },
                        }}
                      >
                        <Link to="pages/password/request">{i18n.t('auth.forgot_password')}?</Link>
                      </Box>
                    </Box>
                  </>
                )}
                {/* STEP 1 ORGANIZATION SELECTOR */}
                {step === 1 && bastions?.length >= 1 && (
                  <Box mt={1}>
                    <Field
                      name="bastion_slug"
                      component={BastionsSelect}
                      label={i18n.t('auth.select_network')}
                    />
                  </Box>
                )}
                {/* STEP 3  FUSION AUTH LOGIN */}
                {step === 3 && (
                  <>
                    <Box item xs={12} mb={6}>
                      <Typography fontSize={26} variant="subtitle1" fontWeight="regular">
                        {i18n.t('auth.sign_in_access')}
                      </Typography>
                      <Typography component="div">
                        <Box fontSize={32} lineHeight={1} fontWeight="bold">
                          {currentBastion?.name}
                        </Box>
                      </Typography>
                    </Box>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={handleBack}
                      data-testid="btn-change-network"
                    >
                      {i18n.t('auth.want_switch_account')}?
                    </Button>
                  </>
                )}
              </Box>
              {errors && errors.bastions && <ErrorMessage>{errors.bastions}</ErrorMessage>}
              <Box mt={6}>
                <Button
                  fullWidth
                  type="submit"
                  data-cy="login"
                  color="primary"
                  loading={loading}
                  disabled={loading}
                  variant="contained"
                  sx={{ textTransform: 'none' }}
                >
                  {getButtonTitle()}
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      </Box>
    </AuthLayout>
  );
};

export default LoginPage;
